import { useCallback, useEffect, useRef, useState } from 'react';
import { searchRegistry } from '../api/skillScoutApi';
import { EmptyState } from '../components/EmptyState';
import { ToolCard } from '../components/ToolCard';
import type { RegistryToolResponse } from '../types/api';

const STACK_OPTIONS = [
  'Python',
  'TypeScript',
  'React',
  'PostgreSQL',
  'Redis',
  'FastAPI',
  'Next.js',
  'Kubernetes',
  'AWS',
];

const PAGE_SIZE = 50;

export function RegistryBrowser() {
  const [query, setQuery] = useState('');
  const [selectedStack, setSelectedStack] = useState<string[]>([]);
  const [tools, setTools] = useState<RegistryToolResponse[]>([]);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const doSearch = useCallback((q: string, stack: string[]) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    searchRegistry(q, stack, 200)
      .then((results) => {
        if (controller.signal.aborted) return;
        setTools(results);
        setDisplayCount(PAGE_SIZE);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setError(String(e));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(query, selectedStack);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, selectedStack, doSearch]);

  const toggleStack = (s: string) => {
    setSelectedStack((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  };

  const visibleTools = tools.slice(0, displayCount);
  const hasMore = tools.length > displayCount;

  return (
    <div className="min-h-screen bg-background text-white">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Registry Browser</h1>
          <p className="text-gray-400 text-sm mt-1">
            Search and filter {tools.length > 0 ? `${tools.length} ` : ''}tools in the registry
          </p>
        </div>

        {/* Search box */}
        <input
          type="search"
          placeholder="Search tools…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-lg bg-surface px-4 py-2 text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-secondary"
          aria-label="Search registry"
        />

        {/* Stack filter chips */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Stack filters">
          {STACK_OPTIONS.map((s) => {
            const active = selectedStack.includes(s);
            return (
              <button
                key={s}
                onClick={() => toggleStack(s)}
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-secondary text-background'
                    : 'bg-surface text-gray-300 hover:bg-card'
                }`}
                aria-pressed={active}
              >
                {s}
              </button>
            );
          })}
        </div>

        {/* Results */}
        {error && (
          <div className="rounded-xl bg-red-900/40 border border-red-700 p-4 text-sm text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-gray-400 text-sm">Searching…</p>
        ) : visibleTools.length === 0 ? (
          <EmptyState message="No tools found — the registry may still be indexing." />
        ) : (
          <>
            <div className="space-y-3">
              {visibleTools.map((tool) => (
                <ToolCard key={tool.slug} tool={tool} />
              ))}
            </div>

            {hasMore && (
              <button
                onClick={() => setDisplayCount((c) => c + PAGE_SIZE)}
                className="w-full rounded-lg bg-surface py-2 text-sm text-gray-300 hover:bg-card transition-colors"
              >
                Load more ({tools.length - displayCount} remaining)
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
